// Analytics orchestrator.
//
// Single entry point for analytics boot. Two tiers:
//   • Umami - cookieless & anonymous (EU-hosted). Sets no cookies, no persistent
//     id, no PII, so it does NOT require consent and loads UNCONDITIONALLY for
//     100% of traffic. Captures visitors who decline/ignore the cookie banner.
//   • GA4 + GTM + Clarity - cookie-based, consent-gated. Only injected once the
//     user has granted consent; subscribes to consent changes so a fresh Accept
//     loads them immediately without a page reload. Clarity additionally records
//     session replays, so it is consent-gated on two grounds, not just cookies.
// Feature code calls `initAnalytics()` once on boot.

import { consent, type ConsentStatus } from '../consent'
import { loadGtm } from './gtm'
import { _resetGoogleConsentForTesting, applyGoogleConsent, loadGa4, syncGaPage } from './ga'
import { loadUmami } from './umami'
import { applyClarityConsent, loadClarity, syncClarityRoute } from './clarity'
import { syncInternalMarker } from './internal'
import { isNonProdBuild } from '../siteEnv'

let unsubscribe: (() => void) | null = null
let active = false

function applyStatus(status: ConsentStatus): void {
  // Google consent mode first, so its defaults precede every Google tag.
  applyGoogleConsent(status)
  if (status !== 'granted') {
    // Consent withdrawn after Clarity loaded: tell it (no-op otherwise).
    applyClarityConsent(status)
    return
  }
  loadGtm()
  loadGa4()
  loadClarity()
}

export function initAnalytics(): void {
  // Non-production builds (staging) are never tracked: no tier loads at all,
  // whatever the consent state. See src/lib/siteEnv.ts.
  if (isNonProdBuild(import.meta.env)) return
  active = true

  // ?internal=1 / ?internal=0 (owner and staff browsers), before any tool loads.
  syncInternalMarker(window.location.search)

  // Cookieless/anonymous - no consent required, fires for everyone.
  loadUmami()

  // Cookie-based - consent-gated.
  applyStatus(consent.get())
  unsubscribe?.()
  unsubscribe = consent.subscribe(applyStatus)
}

/**
 * SPA route change (called by usePageAnalytics). Sends GA4 the redacted page
 * and turns Clarity's replay off on the order pages and back on elsewhere.
 * Umami needs nothing here: it tracks route changes itself and its
 * before-send hook redacts them. No-op before initAnalytics or on non-prod.
 */
export function onRouteChange(url: string, referrer: string, pathname: string): void {
  if (!active) return
  syncGaPage(url, referrer)
  syncClarityRoute(pathname)
  // A visitor who landed on an order page with consent granted has no Clarity
  // yet (loadClarity skips order pages); load it once they leave.
  if (consent.get() === 'granted') loadClarity()
}

export function _teardownAnalyticsForTesting(): void {
  unsubscribe?.()
  unsubscribe = null
  active = false
  _resetGoogleConsentForTesting()
}
